import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { baseUrls } from '../../constants';
import usernameUtils from '../../utils/usernameUtils';

const API = baseUrls.baseUrl;

// ─── Styled components (mismo estilo que login) ────────────────────────────────
const Root = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background: #f4f1e9;
  font-family: 'Avenir Next', 'Trebuchet MS', sans-serif;
  @media (max-width: 640px) { padding: 16px; }
`;

const Shell = styled.section`
  width: min(100%, 960px);
  background: #ffffff;
  border: 1px solid rgba(53, 62, 53, 0.45);
  box-shadow: 0 28px 80px rgba(38, 42, 33, 0.14);
  border-radius: 4px;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr 1fr;
  min-height: 560px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const IllustrationPanel = styled.div`
  background: linear-gradient(180deg, #244f33 0%, #274e35 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 36px;
  gap: 20px;
  @media (max-width: 768px) { padding: 40px 24px; min-height: 240px; }
`;

const CampName = styled.h1`
  margin: 0;
  color: #ffffff;
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 700;
  text-align: center;
  line-height: 1.2;
`;

const CampDate = styled.p`
  margin: 0;
  color: rgba(255,255,255,0.75);
  font-size: 0.95rem;
  text-align: center;
`;

const CampIcon = styled.div`
  width: 220px;
  height: 220px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 5rem;
  overflow: hidden;
  flex-shrink: 0;
`;

const CampLogo = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

const FormPanel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 48px;
  @media (max-width: 768px) { padding: 40px 24px; }
`;

const Form = styled.form`
  width: 100%;
  max-width: 340px;
  display: flex;
  flex-direction: column;
`;

const StepTitle = styled.h2`
  margin: 0 0 6px;
  font-size: 1.5rem;
  font-weight: 600;
  color: #131313;
`;

const StepSubtitle = styled.p`
  margin: 0 0 28px;
  color: #8d8d8d;
  font-size: 0.95rem;
`;

const Field = styled.label`
  position: relative;
  display: block;
  margin-bottom: 20px;
`;

const FieldLabel = styled.span`
  position: absolute;
  top: -10px;
  left: 16px;
  padding: 0 7px;
  color: #8b8b8b;
  background: #ffffff;
  font-size: 0.85rem;
  line-height: 1;
  z-index: 1;
`;

const Input = styled.input`
  width: 100%;
  height: 52px;
  padding: 14px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  color: #1f1f1f;
  font-size: 1rem;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s, box-shadow 0.2s;
  &:focus {
    border-color: #284b31;
    box-shadow: 0 0 0 3px rgba(40,75,49,0.12);
  }
  &:disabled { background: #f5f5f5; color: #999; }
`;

const PrimaryButton = styled.button`
  width: 100%;
  height: 52px;
  margin-top: 4px;
  border: 0;
  border-radius: 4px;
  background: #284b31;
  color: #ffffff;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s, transform 0.15s;
  &:hover:not(:disabled) { background: #203c27; }
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { background: #a0b8a6; cursor: not-allowed; }
`;

const BackLink = styled.button`
  background: none;
  border: none;
  color: #284b31;
  font-size: 0.9rem;
  cursor: pointer;
  padding: 0;
  margin-top: 14px;
  text-decoration: underline;
  &:hover { color: #203c27; }
`;

const ErrorBox = styled.div`
  margin-bottom: 16px;
  padding: 10px 14px;
  border: 1px solid #efc3c3;
  border-radius: 8px;
  color: #9d2f2f;
  background: #fff3f3;
  font-size: 0.9rem;
`;

const SuccessBox = styled.div`
  text-align: center;
  padding: 16px;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 12px;
`;

const SuccessTitle = styled.h2`
  margin: 0 0 8px;
  font-size: 1.4rem;
  font-weight: 700;
  color: #284b31;
`;

const SuccessText = styled.p`
  margin: 0;
  color: #555;
  font-size: 0.95rem;
  line-height: 1.5;
`;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('es-ES', {
    day: 'numeric', month: 'long', year: 'numeric',
  });
};

// ─── Pasos del flujo ──────────────────────────────────────────────────────────
const STEP = { WELCOME: 'welcome', EMAIL: 'email', LOGIN: 'login', REGISTER: 'register', SUCCESS: 'success' };

// ─── Componente principal ─────────────────────────────────────────────────────
export default function InvitePage() {
  const { campId } = useParams();
  const [camp, setCamp]   = useState(null);
  const [campLoading, setCampLoading] = useState(true);
  const [campError, setCampError]     = useState(false);

  const [step, setStep]         = useState(STEP.WELCOME);
  const [email, setEmail]       = useState('');
  const [fullname, setFullname] = useState('');
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  // Login fields
  const [loginPassword, setLoginPassword] = useState('');

  // Register fields
  const [regUsername, setRegUsername]   = useState('');
  const [regFullname, setRegFullname]   = useState('');
  const [regPassword, setRegPassword]   = useState('');
  const [regPassword2, setRegPassword2] = useState('');

  useEffect(() => {
    axios.get(`${API}/camp/${campId}`)
      .then(r => { setCamp(r.data); setCampLoading(false); })
      .catch(() => { setCampError(true); setCampLoading(false); });
  }, [campId]);

  const handleCheckEmail = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/invite/check-email`, { email });
      if (data.exists) {
        setFullname(data.fullname || '');
        setStep(STEP.LOGIN);
      } else {
        setStep(STEP.REGISTER);
      }
    } catch {
      setError('No se pudo verificar el email. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/invite/login`, { email, password: loginPassword, campId });
      setStep(STEP.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.message || 'Credenciales incorrectas.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    const normalizedUsername = usernameUtils.normalize(regUsername);

    if (!usernameUtils.isValid(normalizedUsername)) {
      setError(usernameUtils.validationMessage);
      return;
    }

    if (regPassword !== regPassword2) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/invite/register`, {
        email, username: normalizedUsername, fullname: regFullname, password: regPassword, campId,
      });
      setStep(STEP.SUCCESS);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al crear la cuenta.');
    } finally {
      setLoading(false);
    }
  };

  // ── Panel izquierdo ──
  const leftPanel = (
    <IllustrationPanel>
      <CampIcon>
        {camp?.logoUrl ? <CampLogo src={camp.logoUrl} alt={camp.name} /> : '⛺'}
      </CampIcon>
      {campLoading
        ? <CircularProgress sx={{ color: 'rgba(255,255,255,0.6)' }} />
        : campError
          ? <CampName>Campamento</CampName>
          : <>
              <CampName>{camp?.name}</CampName>
              {camp?.dateInit && (
                <CampDate>
                  {formatDate(camp.dateInit)}
                  {camp.dateEnd ? ` — ${formatDate(camp.dateEnd)}` : ''}
                </CampDate>
              )}
            </>
      }
    </IllustrationPanel>
  );

  // ── Panel derecho según paso ──
  const renderStep = () => {
    if (step === STEP.WELCOME) return (
      <FormPanel>
        <Form onSubmit={e => { e.preventDefault(); setStep(STEP.EMAIL); }}>
          <StepTitle>¡Fuiste invitado!</StepTitle>
          <StepSubtitle>
            {camp ? `Únete a "${camp.name}" como juez.` : 'Acepta la invitación para unirte al campamento.'}
          </StepSubtitle>
          <PrimaryButton type="submit" disabled={campLoading}>
            Aceptar Invitación
          </PrimaryButton>
        </Form>
      </FormPanel>
    );

    if (step === STEP.EMAIL) return (
      <FormPanel>
        <Form onSubmit={handleCheckEmail}>
          <StepTitle>Tu email</StepTitle>
          <StepSubtitle>Ingresa tu email para continuar.</StepSubtitle>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Field>
            <FieldLabel>Email</FieldLabel>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
          </Field>
          <PrimaryButton type="submit" disabled={loading}>
            {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Continuar'}
          </PrimaryButton>
          <BackLink type="button" onClick={() => { setStep(STEP.WELCOME); setError(''); }}>
            ← Volver
          </BackLink>
        </Form>
      </FormPanel>
    );

    if (step === STEP.LOGIN) return (
      <FormPanel>
        <Form onSubmit={handleLogin}>
          <StepTitle>Bienvenido{fullname ? `, ${fullname.split(' ')[0]}` : ''}</StepTitle>
          <StepSubtitle>Ingresa tu contraseña para unirte al campamento.</StepSubtitle>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Field>
            <FieldLabel>Email</FieldLabel>
            <Input type="email" value={email} disabled />
          </Field>
          <Field>
            <FieldLabel>Contraseña</FieldLabel>
            <Input
              type="password" value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              required autoFocus
            />
          </Field>
          <PrimaryButton type="submit" disabled={loading}>
            {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Unirme al campamento'}
          </PrimaryButton>
          <BackLink type="button" onClick={() => { setStep(STEP.EMAIL); setError(''); }}>
            ← Usar otro email
          </BackLink>
        </Form>
      </FormPanel>
    );

    if (step === STEP.REGISTER) return (
      <FormPanel>
        <Form onSubmit={handleRegister}>
          <StepTitle>Crear cuenta</StepTitle>
          <StepSubtitle>No encontramos tu email. Crea una cuenta para unirte.</StepSubtitle>
          {error && <ErrorBox>{error}</ErrorBox>}
          <Field>
            <FieldLabel>Email</FieldLabel>
            <Input type="email" value={email} disabled />
          </Field>
          <Field>
            <FieldLabel>Nombre completo</FieldLabel>
            <Input
              type="text" value={regFullname}
              onChange={e => setRegFullname(e.target.value)}
              required autoFocus
            />
          </Field>
          <Field>
            <FieldLabel>Usuario</FieldLabel>
            <Input
              type="text" value={regUsername}
              onChange={e => setRegUsername(usernameUtils.sanitizeInput(e.target.value))}
              minLength={3}
              maxLength={30}
              pattern={usernameUtils.regex.source}
              autoCapitalize="none"
              required
            />
          </Field>
          <Field>
            <FieldLabel>Contraseña</FieldLabel>
            <Input
              type="password" value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel>Confirmar contraseña</FieldLabel>
            <Input
              type="password" value={regPassword2}
              onChange={e => setRegPassword2(e.target.value)}
              required
            />
          </Field>
          <PrimaryButton type="submit" disabled={loading}>
            {loading ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Crear cuenta y unirme'}
          </PrimaryButton>
          <BackLink type="button" onClick={() => { setStep(STEP.EMAIL); setError(''); }}>
            ← Usar otro email
          </BackLink>
        </Form>
      </FormPanel>
    );

    if (step === STEP.SUCCESS) return (
      <FormPanel>
        <SuccessBox>
          <SuccessIcon>🎉</SuccessIcon>
          <SuccessTitle>¡Bienvenido al campamento!</SuccessTitle>
          <SuccessText>
            Te has unido exitosamente a <strong>{camp?.name}</strong>.<br />
            Ya puedes iniciar sesión en la aplicación.
          </SuccessText>
        </SuccessBox>
      </FormPanel>
    );
  };

  return (
    <Root>
      <Shell>
        {leftPanel}
        {renderStep()}
      </Shell>
    </Root>
  );
}
